import { useContext, useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import IGetApiKeysDTO from "../../../../models/DTO/IGetApiKeysDTO";
import IOnGetApiKeysDTO from "../../../../models/DTO/IOnGetApiKeysDTO";
import IOnProcessApiKeyDTO from "../../../../models/DTO/IOnProcessApiKeyDTO";
import IProcessApiKeyDTO from "../../../../models/DTO/IProcessApiKeyDTO";
import EndpointsEnum from "../../../../models/EndpointsEnum";
import IApi_Key from "../../../../models/IApi_Key";
import INotification from "../../../../models/INotification";
import NotificationLevelEnum from "../../../../models/NotificationLevelEnum";
import NotificationPositionEnum from "../../../../models/NotificationPositionEnum";
import OwButton from "../../../shared/OwButton/OwButton";
import OwLabel from "../../../shared/OwLabel/OwLabel";
import { ApiKeySidebar } from "../ApiKeySidebar/ApiKeySidebar";
import "./ApiKey.scss";
import UserContext from "../../../../contexts/UserContext";
import SupplierContext from "../../../../contexts/SupplierContext";
import { SocketContext } from "../../../../contexts/SocketContext";
import UiContext from "../../../../contexts/UiContext";
import IUiSocketSetup from "../../../../models/IUiSocketSetup";

type Props = {};

/** Copies the text to the clipboard, falling back to a hidden text area where the Clipboard API is missing. */
const copyText = async (text: string) => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);

        return;
    }

    const textArea = document.createElement("textarea");

    textArea.value = text;
    textArea.style.position = "fixed";
    textArea.style.left = "-999999px";
    document.body.appendChild(textArea);
    textArea.select();

    const copied = document.execCommand("copy");

    textArea.remove();

    if (!copied) {
        throw new Error("Copy command failed");
    }
};

/** How long a delete waits for the server before the list lets the user try again. */
const DELETE_TIMEOUT_MS = 15000;

/**
 * The business's API keys as a list: name, the key with a copy button, when it was made, rename and delete.
 * Create and rename open the key sidebar, which is the only place the secret is shown.
 */
export const ApiKey = (props: Props) => {
    const intl = useIntl();
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);
    const socket = useContext(SocketContext)!;
    const uiContext = useContext(UiContext);

    const [apiKeys, setApiKeys] = useState<IApi_Key[]>([]);
    const [loaded, setLoaded] = useState(false);
    const [confirmingDelete, setConfirmingDelete] = useState<string | null>(null);
    const [copiedKey, setCopiedKey] = useState<string | null>(null);
    const confirmTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const copiedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    // The one delete waiting on the server, so a second delete cannot overlap it and its listener
    // never outlives the request. The sidebar shares the response event, so it is not a standing listener.
    const pendingDelete = useRef<((data: IOnProcessApiKeyDTO) => void) | null>(null);
    const deleteTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    const t = (id: string, values?: Record<string, any>) => intl.formatMessage({ id, defaultMessage: `_${id}_` }, values);

    const getApiKeys = () => {
        const payload: IGetApiKeysDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
        };

        socket.emit(EndpointsEnum.SuppliersGetApiKeys, payload);
    };

    const onGetApiKeys = (data: IOnGetApiKeysDTO) => {
        setApiKeys(data.apiKeys || []);
        setLoaded(true);
    };

    const sockets: IUiSocketSetup[] = [{ endpoint: EndpointsEnum.SuppliersOnGetApiKeys, fn: onGetApiKeys }];

    useEffect(() => {
        for (const _socket of sockets) {
            socket.on(_socket.endpoint, _socket.fn);
        }

        getApiKeys();

        return () => {
            for (const _socket of sockets) {
                socket.off(_socket.endpoint, _socket.fn);
            }

            if (confirmTimer.current) {
                clearTimeout(confirmTimer.current);
            }

            if (copiedTimer.current) {
                clearTimeout(copiedTimer.current);
            }

            clearPendingDelete();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const clearPendingDelete = () => {
        if (pendingDelete.current) {
            socket.off(EndpointsEnum.SuppliersOnProcessApiKey, pendingDelete.current);
            pendingDelete.current = null;
        }

        if (deleteTimer.current) {
            clearTimeout(deleteTimer.current);
            deleteTimer.current = null;
        }
    };

    const cancelSidebar = () => {
        uiContext.setSidebarComponent(null);
        uiContext.toggleSidebar(false);
        getApiKeys();
    };

    const openSidebar = (apiKey?: IApi_Key) => {
        uiContext.setSidebarComponent(<ApiKeySidebar cancelFn={cancelSidebar} apiKey={apiKey} />);
        uiContext.toggleSidebar(true);
    };

    const copyKey = async (apiKey: IApi_Key) => {
        try {
            await copyText(apiKey.api_key);
            setCopiedKey(apiKey.api_key);

            if (copiedTimer.current) {
                clearTimeout(copiedTimer.current);
            }

            copiedTimer.current = setTimeout(() => setCopiedKey(null), 2000);
        } catch (err) {
            console.error("Failed to copy text: ", err);
        }
    };

    const onProcessApiKey = () => {
        clearPendingDelete();

        const notification: INotification = {
            message: "successfully_deleted_api_key",
            position: NotificationPositionEnum.bc,
            level: NotificationLevelEnum.success,
        };

        uiContext.notifyFn(null, notification);
        getApiKeys();
    };

    const remove = (apiKey: IApi_Key) => {
        if (pendingDelete.current) {
            return;
        }

        if (confirmingDelete !== apiKey.api_key) {
            setConfirmingDelete(apiKey.api_key);

            if (confirmTimer.current) {
                clearTimeout(confirmTimer.current);
            }

            confirmTimer.current = setTimeout(() => setConfirmingDelete(null), 4000);

            return;
        }

        setConfirmingDelete(null);

        const payload: IProcessApiKeyDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            deleteFlag: true,
            api_key: apiKey.api_key,
        };

        pendingDelete.current = onProcessApiKey;
        socket.once(EndpointsEnum.SuppliersOnProcessApiKey, onProcessApiKey);
        socket.emit(EndpointsEnum.SuppliersProcessApiKey, payload);
        deleteTimer.current = setTimeout(clearPendingDelete, DELETE_TIMEOUT_MS);
    };

    const countLabel = apiKeys.length === 1 ? t("one_key") : t("keys_count", { count: apiKeys.length });

    return (
        <div className={"ApiKeys"} data-testid={"api-keys"}>
            <div className={"ApiKeys-bar"}>
                {loaded ? (
                    <span className={"SettingsChip" + (apiKeys.length ? " on" : "")} data-testid={"api-keys-count"}>
                        <i className={"fad fa-key"} aria-hidden={true} />
                        {countLabel}
                    </span>
                ) : null}
                <a className={"ApiKeys-docs"} href={"https://docs.orderwerks.com/"} target={"_blank"} rel={"noreferrer"}>
                    <i className={"fad fa-external-link-alt"} aria-hidden={true} />
                    <OwLabel className={"pointer"} title={"view_api_docs"} noBottomMargin={true} />
                </a>
                <span className={"ApiKeys-spacer"} />
                <OwButton
                    title={"create_key"}
                    leftIconClass={"fas fa-plus"}
                    fn={() => openSidebar()}
                    disableWidthOnClick={true}
                    testId={"api-keys-create"}
                />
            </div>

            <div className={"ApiKeys-list"} data-testid={"api-keys-list"}>
                {loaded && !apiKeys.length ? (
                    <div className={"ApiKeys-empty"} data-testid={"api-keys-empty"}>
                        <i className={"fad fa-key"} aria-hidden={true} />
                        <div>
                            <div className={"ApiKeys-empty-title"}>
                                <OwLabel title={"no_api_keys_yet"} noBottomMargin={true} />
                            </div>
                            <div className={"ApiKeys-empty-help"}>
                                <OwLabel title={"help_text_no_api_keys"} noBottomMargin={true} />
                            </div>
                        </div>
                    </div>
                ) : null}

                {apiKeys.map((apiKey) => {
                    const copied = copiedKey === apiKey.api_key;
                    const confirming = confirmingDelete === apiKey.api_key;

                    return (
                        <div className={"ApiKeys-row"} key={apiKey.api_key} data-testid={`api-key-${apiKey.api_key}`}>
                            <span className={"ApiKeys-icon"}>
                                <i className={"fad fa-key"} aria-hidden={true} />
                            </span>
                            <div className={"ApiKeys-main"}>
                                <div className={"ApiKeys-name"}>{apiKey.name}</div>
                                <div className={"ApiKeys-meta"}>
                                    <code className={"ApiKeys-key"}>{apiKey.api_key}</code>
                                    <OwButton
                                        centerIconClass={copied ? "fas fa-check text-success" : "far fa-copy"}
                                        className={"btn-link text-muted ApiKeys-copy"}
                                        ariaLabel={`${t("copy_key")} ${apiKey.name}`}
                                        hoverTitle={copied ? t("key_copied") : t("copy_key")}
                                        fn={() => copyKey(apiKey)}
                                        disableWidthOnClick={true}
                                        testId={`api-key-copy-${apiKey.api_key}`}
                                    />
                                    {apiKey.created_at ? (
                                        <span className={"ApiKeys-created"}>
                                            {t("created")} {intl.formatDate(apiKey.created_at, { dateStyle: "medium" })}
                                        </span>
                                    ) : null}
                                </div>
                            </div>
                            <div className={"ApiKeys-actions"}>
                                {confirming ? (
                                    <>
                                        <OwButton
                                            title={"cancel"}
                                            className={"btn-link text-muted btn-sm"}
                                            fn={() => setConfirmingDelete(null)}
                                            disableWidthOnClick={true}
                                            disableDblClickPrevention={true}
                                            testId={`api-key-cancel-delete-${apiKey.api_key}`}
                                        />
                                        <OwButton
                                            title={"confirm_delete"}
                                            className={"btn-danger btn-sm"}
                                            leftIconClass={"fad fa-trash-alt"}
                                            fn={() => remove(apiKey)}
                                            disableWidthOnClick={true}
                                            testId={`api-key-confirm-delete-${apiKey.api_key}`}
                                        />
                                    </>
                                ) : (
                                    <>
                                        <OwButton
                                            centerIconClass={"fad fa-pen-to-square"}
                                            className={"btn-link text-muted"}
                                            ariaLabel={`${t("rename")} ${apiKey.name}`}
                                            hoverTitle={t("rename")}
                                            fn={() => openSidebar(apiKey)}
                                            disableWidthOnClick={true}
                                        />
                                        <OwButton
                                            centerIconClass={"fad fa-trash-alt"}
                                            className={"btn-link text-muted"}
                                            ariaLabel={`${t("delete")} ${apiKey.name}`}
                                            hoverTitle={t("delete")}
                                            fn={() => remove(apiKey)}
                                            disableWidthOnClick={true}
                                            disableDblClickPrevention={true}
                                            testId={`api-key-delete-${apiKey.api_key}`}
                                        />
                                    </>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
